import type { ReactNode } from "react";
import { LeftOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getRestaurantName } from "../../../../lib/restaurant";
import { useAppSelector } from "../../../../store/hooks";

/**
 * Лявата колона на „Обедно меню" (десктоп) — рамката на sidebar-а в справките:
 * „‹ Към масите" най-горе (Esc прави същото — ESC_PARENT_PAGES, без подсказка,
 * както в справките), логото и името, списъкът с менютата и фиксирано дъно с
 * „Ново обедно меню".
 */
export const LunchMenusSidebar = ({
  onNew,
  children,
}: {
  /** „Ново обедно меню" във фиксираното дъно; липсва при празния екран (там е в средата) */
  onNew?: () => void;
  children: ReactNode;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const venueName = useAppSelector(
    (s) => s.auth.user?.restaurant_display_name ?? "",
  );

  return (
    <aside className="flex w-[clamp(300px,30%,420px)] shrink-0 flex-col border-r border-pos-line bg-pos-panel">
      <div className="flex flex-col px-4 pt-4">
        <button
          type="button"
          onClick={() => navigate("/ordering")}
          className="mb-3.5 flex cursor-pointer items-center gap-2 self-start rounded-md border-0 bg-transparent px-2.5 py-1.5 text-xs font-medium text-pos-body transition-colors hover:bg-pos-line2"
        >
          <LeftOutlined className="text-[11px]" />
          {t("lunchMenu.backToTables")}
        </button>
        <div className="flex items-center gap-2.5 border-b border-pos-line px-2.5 pb-4">
          <img
            src="/logo-ts.png"
            alt="UnrealSoft"
            className="h-8 w-8 shrink-0 object-contain"
          />
          <div className="flex min-w-0 flex-col gap-0.5 leading-[1.2]">
            <span className="truncate text-[15px] font-extrabold text-pos-ink">
              {venueName || getRestaurantName()}
            </span>
            <span className="truncate text-xs text-pos-soft2">
              {t("modules.ordering")} · {t("lunchMenu.title")}
            </span>
          </div>
        </div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-4 pb-4 pt-1">
        {children}
      </div>
      {/* Фиксирано дъно на колоната: списъкът скролира над него, а
          „Ново обедно меню" се вижда винаги, колкото и менюта да има */}
      {onNew && (
        <div className="shrink-0 border-t border-pos-line bg-pos-panel px-4 py-3">
          <button
            type="button"
            onClick={onNew}
            className="inline-flex h-10 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[10px] border-0 bg-pos-accent px-3.5 text-[14px] font-bold text-white transition-colors hover:bg-pos-accent-ink"
          >
            <PlusOutlined />
            {t("lunchMenu.newMenu")}
          </button>
        </div>
      )}
    </aside>
  );
};
